<script lang="ts" setup>
import ReplyList from "@/components/mobile/reply/ReplyList.vue";

import { onBeforeMount, ref, UnwrapRef } from "vue";
import { showFailToast } from "vant";
import { useMoment } from "@/stores/moment";
import { useRoute, useRouter } from "vue-router";
import { useGlobalUser } from "@/stores/globalUser";
import MomentActions from "@/components/mobile/popup/MomentActions.vue";
import ReplyInput, {
  ReplyInputInstance,
} from "@/components/mobile/reply/ReplyInput.vue";
import ThreadsNav from "@/components/mobile/ThreadsNav.vue";
import i18n from "@/locales";
import { sessionStorageSet } from "@/tools/sessionStorage";
import { ReplyEntityType } from "@/enums";
import { Moment, Reply } from "@/types/entity";
import { useMate } from "@/stores/mate";

const globalUser = useGlobalUser();
// 状态
const momentStore = useMoment();
const route = useRoute();
const router = useRouter();
const moment = ref<Moment>();
const mateStore = useMate();
// 底部输入框状态
const replyTarget = ref<Reply>(); // 底部输入框的回复目标
let defaultReply: Reply | undefined;
const replyInputRef = ref<ReplyInputInstance>();

export interface ReplyTree extends Reply {
  replyTo?: Reply; // parentReply
  children: ReplyTree[];
}

const enableActions = ref(true);
// Lifecycle
const isMomentLoading = ref(false);
onBeforeMount(async () => {
  try {
    isMomentLoading.value = true;
    const uuid = route.params.uuid.toString();
    const { from } = route.query;
    if (from === "requestList") {
      enableActions.value = false;
      moment.value = await momentStore.getOrFetchMomentItem(uuid, false);
    } else {
      moment.value = await momentStore.getOrFetchMomentItem(uuid, true);
    }
    if (moment.value?.type === 2)
      mateStore.changeCurrentMate(moment.value.mate.id);
  } catch (e) {
    showFailToast(i18n.global.t("Message.dynamicDisappearedInto"));
  } finally {
    isMomentLoading.value = false;
  }

  defaultReply = {
    ...moment.value!.reply,
    mate: moment.value!.mate,
  };
  replyTarget.value = defaultReply;
});

// Events Handler
const onReplyBtnClick = (replay: Reply) => {
  replyTarget.value = replay;
  replyInputRef.value?.focus();
};
// Action Popup
const showActions = ref(false);
const toOperateMoment = ref<Moment | null>(null);
const toOperateReply = ref<Reply | null>(null);
const onReplyActionClick = (reply: Reply) => {
  toOperateMoment.value = null;
  toOperateReply.value = reply;
  showActions.value = true;
};
const onMomentActionClick = (moment: UnwrapRef<Moment>) => {
  toOperateReply.value = null;
  toOperateMoment.value = moment;
  showActions.value = true;
};

const afterDelete = () => {
  sessionStorageSet("momentDeleted", moment.value?.uuid);
  router.back();
};
</script>
<template>
  <div class="h-full grid grid-rows-[auto_1fr_auto]">
    <threads-nav show-back :show-center="false"> </threads-nav>
    <div
      v-if="isMomentLoading || !moment"
      class="flex items-center justify-center"
    >
      <span class="loading loading-ring"></span>
    </div>
    <div v-else class="w-full overflow-y-auto relative px-4 pt-2">
      <!-- 当前Thread -->
      <div class="flex flex-col gap-2">
        <TreeholeThreadItem
          v-if="moment.type === 2"
          :key="moment.uuid"
          :moment="moment"
          enable-audio-play
          enable-show-more-link
          :show-reply-list="false"
          @reaction-click="onMomentActionClick(moment)"
          isTreehole
        />
        <thread-item1
          v-else
          :avatar-url="moment.mate.mediaUrl"
          :chat-message-list="moment.chatMessagezz"
          :content="moment.text"
          :enable-reaction-btn="!!(globalUser.isLogin && enableActions)"
          :enable-text-collapsed="false"
          :from-mate="moment.fromMate"
          :gal-game="moment.galGame"
          :impression-reactions="[]"
          :mate-id="moment.mate.uuid"
          :mate-tone-id="moment.mate.mateToneId"
          :media-list="moment.mediazz"
          :mentionMatezz="moment.mentionMatezz"
          :moment="moment"
          :reply="moment.reply"
          :show-comment="true"
          :time="moment.dtCreate"
          :title="moment.mate.name"
          :type="moment.mate.type"
          :uuid="moment.uuid"
          :visibility="moment.visibility"
          enable-audio-play
          show-coin-vote
          @reaction-click="onMomentActionClick(moment)"
          @comment-click="
            onReplyBtnClick({ ...moment.reply, mate: moment.mate })
          "
        />
        <div class="flex items-center gap-1">
          <i class="iconify mingcute--comment-line" />
          {{ moment.reply.replyCount }}
        </div>
        <ReplyList
          v-if="moment.type !== 2"
          :moment="moment"
          :root-id="moment?.reply.id"
          @on-reply-click="onReplyBtnClick"
          @on-reaction-click="onReplyActionClick"
        />
        <TreeholeReplyList
          v-else
          :moment="moment"
          :root-id="moment?.reply.id"
          @on-reply-click="onReplyBtnClick"
          @on-reaction-click="onReplyActionClick"
          :is-treehole="moment.type === 2"
        />
      </div>
    </div>
    <!-- 底部输入框 -->
    <reply-input
      v-if="moment && enableActions"
      ref="replyInputRef"
      :reply-target="replyTarget"
      :show-tip="defaultReply?.id !== replyTarget?.id"
      @reply-aborted="replyTarget = defaultReply"
      :entity-type="ReplyEntityType.Moment"
      :entity="moment"
    />
    <moment-actions
      v-model:show="showActions"
      :moment="toOperateMoment"
      :reply="toOperateReply"
      :replyMoment="moment"
      @after-delete="afterDelete"
    />
  </div>
</template>
